'use strict';

import React, { useMemo } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { ENABLE_PIPELINE_STATUS } from '@/config/env.config';
import { usePermissions } from '@/hooks/permissions.hook';
import * as permissions from '@/security/permissionTypes';
import { usePipelineStatusActivityQuery } from '@/redux/services/portal/pipelineStatus.api';
import { processActivityStripRows, formatActivityStripLine } from './activityStripUtils';
import './ActivityStrip.scss';

const POLL_MS = 20000;
const FETCH_TAKE = 30;
const VISIBLE_MAX = 8;

/**
 * Global pipeline activity strip (admin-only). Data from GET /api/v1/admin/pipeline-status/activity — polling only, no SignalR.
 */
export function ActivityStrip() {
	const { checkUserHasPermissions } = usePermissions();
	const canViewAdmin = checkUserHasPermissions(permissions.CAN_VIEW_ADMIN);

	const canShowActivityStrip = ENABLE_PIPELINE_STATUS && canViewAdmin;

	const { data, isLoading, isFetching, error } = usePipelineStatusActivityQuery(
		{ take: FETCH_TAKE },
		{
			skip: !canShowActivityStrip,
			pollingInterval: POLL_MS,
			refetchOnMountOrArgChange: true,
		}
	);

	const items = useMemo(() => {
		const rows = Array.isArray(data) ? data : [];
		return processActivityStripRows(rows, VISIBLE_MAX);
	}, [data]);

	if (!canShowActivityStrip) {
		return null;
	}

	if (error) {
		return null;
	}

	const showLoadingBar = (isLoading || isFetching) && !data;
	const showEmpty = !isLoading && !data?.length;
	const hideCompletely = showEmpty || items.length === 0;

	if (hideCompletely && !showLoadingBar) {
		return null;
	}

	return (
		<div className="activity-strip" role="region" aria-label="Recent pipeline activity">
			{showLoadingBar && (
				<LinearProgress
					className="activity-strip__loading"
					variant="indeterminate"
					sx={{ height: 2 }}
				/>
			)}
			{items.length > 0 && (
				<Box
					className="activity-strip__scroll"
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 2,
						overflowX: 'auto',
						py: 0.5,
						px: 1,
						minHeight: 28,
					}}
				>
					{items.map((row, idx) => (
						<Typography
							key={row.id != null ? String(row.id) : `activity-${idx}-${row.subjectId}-${row.stage}`}
							variant="caption"
							component="span"
							className="activity-strip__item"
							noWrap
							sx={{
								flex: '0 0 auto',
								maxWidth: 360,
								opacity: isFetching ? 0.85 : 1,
								transition: 'opacity 0.2s',
							}}
							title={formatActivityStripLine(row)}
						>
							{formatActivityStripLine(row)}
						</Typography>
					))}
				</Box>
			)}
		</div>
	);
}

export default ActivityStrip;
